import React, { useEffect, useState } from "react";
import "./AdminScholarshipList.scss";
import axios from "axios";

import { utils, writeFile } from "xlsx";
import { DateRangePicker } from "rsuite";
import { Input, SelectPicker } from "rsuite";

import Button from "../../components/Button/Button";
import Loader from "../../components/Loader/Loader";
import EntranceTable from "../../components/EntranceTable/EntranceTable";
import { useSelector } from "react-redux";

// Icons
import {
  FaUsers,
  FaGraduationCap,
  FaSearch,
  FaFilter,
  FaDownload,
  FaSchool,
  FaCalendarAlt,
  FaHome,
  FaUtensils,
  FaBus,
  FaBed,
  FaExclamationTriangle,
  FaFileExcel,
  FaListAlt,
  FaChartBar,
  FaInfoCircle,
  FaDatabase,
  FaUserGraduate,
  FaBookOpen,
  FaClipboardList,
  FaTrophy,
  FaMedal,
  FaStar,
} from "react-icons/fa";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminScholarshipList = () => {
  //RBAC code block
  const allPermissions = useSelector((state) => state.permissions);
  const permissions = allPermissions
    .find((p) => p.name === "Admissions")
    ?.dropdowns.find((dd) => dd.name === "Scholarship List")?.permissions;

  const [isLoading, setIsLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [dateRange, setDateRange] = useState([]);
  const [selectedClass, setSelectedClass] = useState("All Classes");
  const [hostelFilter, setHostelFilter] = useState("All");
  const [mathsMarksFilter, setMathsMarksFilter] = useState("All");
  const [scienceMarksFilter, setScienceMarksFilter] = useState("All");
  const [selectedSession, setSelectedSession] = useState("2025-2026");
  const [branch, setBranch] = useState("Baramati");

  const branches = ["Baramati", "Pune", "Nagar", "Ranchi", "All"];
  const hostelOptions = ["All", "Yes", "No"];
  const marksOptions = [
    "All",
    "Above 95",
    "Between 90 and 95",
    "Between 85 and 90",
    "Between 80 and 85",
    "Between 75 and 80",
    "Between 70 and 75",
  ];

  const classOptions = [
    "All Classes",
    "Class 6th",
    "Class 7th",
    "Class 8th",
    "Class 9th",
    "Class 10th",
    "Class 11th",
    "Class 12th",
    "Neet Repeater",
    "JEE Repeater",
  ];

  const sessionOptions = ["2024-2025", "2025-2026", "2026-2027", "All"];

  const classData = classOptions.map((cls) => ({
    label: cls,
    value: cls,
    role: cls === "All Classes" ? "All" : "Class",
  }));

  const filterMarks = (marks, filter) => {
    switch (filter) {
      case "Above 95":
        return marks > 95;
      case "Between 90 and 95":
        return marks > 90 && marks <= 95;
      case "Between 85 and 90":
        return marks > 85 && marks <= 90;
      case "Between 80 and 85":
        return marks > 80 && marks <= 85;
      case "Between 75 and 80":
        return marks > 75 && marks <= 80;
      case "Between 70 and 75":
        return marks > 70 && marks <= 75;
      default:
        return true;
    }
  };

  const handleDateRangeChange = (item) => {
    if (!item) {
      setDateRange([]);
    } else {
      setDateRange(item);
    }
  };

  const handleClassFilterChange = (value) => {
    setSelectedClass(value);
  };

  const handleSendEmails = async (emails, subject, text) => {
    if (!emails || emails.length === 0) {
      alert("Please select at least one recipient.");
      return;
    }

    if (!subject.trim() || !text.trim()) {
      alert("Subject and email content cannot be empty.");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to send this email to the selected recipients?"
      )
    ) {
      return;
    }

    setIsLoading(true);

    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));

      await axios.post(
        `${BE_URL}/sendInfoEmailsSch`,
        {
          emails,
          subject,
          text,
        },
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        }
      );

      alert("Emails sent successfully.");
      setIsLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        const newTokens = await refreshToken();
        if (newTokens) {
          handleSendEmails(emails, subject, text);
        }
        setIsLoading(false);
      } else {
        console.error("Error sending emails:", error);
        alert("Failed to send emails. Please try again later.");
        setIsLoading(false);
      }
    }
  };

  const fetchEnquiries = async (selectedSession) => {
    setIsLoading(true);
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      let response = null;
      if (branch === "All" && selectedSession === "All") {
        response = await fetch(`${BE_URL}/getAllscholarships`, {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        });
      } else if (branch === "All") {
        response = await fetch(
          `${BE_URL}/getAllscholarships?session=${selectedSession}`,
          {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          }
        );
      } else if (selectedSession === "All") {
        response = await fetch(
          `${BE_URL}/getAllscholarships?branch=${branch}`,
          {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          }
        );
      } else {
        response = await fetch(
          `${BE_URL}/getAllscholarships?session=${selectedSession}&branch=${branch}`,
          {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          }
        );
      }

      if (!response.ok) {
        if (response.status === 403) {
          alert("Unauthorized Access");
        }
        if (response.status === 401) {
          const newTokens = await refreshToken();
          if (newTokens) {
            return fetchEnquiries(selectedSession);
          }
        }
        throw new Error("Failed to fetch Scholarships forms");
      }

      const data = await response.json();
      setEnquiries(data);
      setFilteredEnquiries([]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const refreshToken = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await fetch("https://backend.classerp.in/refreshToken", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: tokens.refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }

      const newTokens = await response.json();
      localStorage.setItem("ERPTokens", JSON.stringify(newTokens));
      return newTokens;
    } catch (error) {
      console.error("Error refreshing token:", error);
      alert("User logged out. Please login again");
      return null;
    }
  };

  useEffect(() => {
    fetchEnquiries(selectedSession);
  }, [selectedSession, branch]);

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    dateRange,
    selectedClass,
    hostelFilter,
    mathsMarksFilter,
    scienceMarksFilter,
    enquiries,
  ]);

  const applyFilters = () => {
    let filtered = enquiries.filter((enquiry) => {
      const enquiryDate = new Date(enquiry.timestamp);
      const [start, end] = dateRange;

      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearchTerm = searchTerm
        ? enquiry.first_name.toLowerCase().includes(searchTermLower) ||
          enquiry.last_name.toLowerCase().includes(searchTermLower) ||
          enquiry.email.toLowerCase().includes(searchTermLower) ||
          enquiry.roll_no.toLowerCase().includes(searchTermLower) ||
          enquiry.whatsapp.toLowerCase().includes(searchTermLower) ||
          enquiry.aadhar.toLowerCase().includes(searchTermLower)
        : true;

      const matchesClass =
        selectedClass === "All Classes" || enquiry.adm_class === selectedClass;

      const matchesHostel =
        hostelFilter === "All" || enquiry.hostel === hostelFilter;

      const matchesMathsMarks =
        mathsMarksFilter === "All" ||
        filterMarks(enquiry.prev_math, mathsMarksFilter);

      const matchesScienceMarks =
        scienceMarksFilter === "All" ||
        filterMarks(enquiry.prev_sci, scienceMarksFilter);

      const matchesDateRange =
        (!start || enquiryDate >= start) && (!end || enquiryDate <= end);

      return (
        matchesSearchTerm &&
        matchesClass &&
        matchesHostel &&
        matchesMathsMarks &&
        matchesScienceMarks &&
        matchesDateRange
      );
    });

    setFilteredEnquiries(filtered);
  };

  useEffect(() => {
    setIsLoading(true);
    if (sortOrder === "desc") {
      setEnquiries((prevEnquiries) =>
        [...prevEnquiries].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        )
      );
    } else {
      setEnquiries((prevEnquiries) =>
        [...prevEnquiries].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        )
      );
    }
    setIsLoading(false);
  }, [sortOrder]);

  const downloadExcel = () => {
    setIsLoading(true);
    const ws = utils.json_to_sheet(filteredEnquiries);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Scholarships");
    writeFile(wb, "Scholarships.xlsx");
    setIsLoading(false);
  };

  // Calculate statistics
  const getStatistics = () => {
    const hostelStudents = filteredEnquiries.filter(
      (enquiry) => enquiry.hostel === "Yes"
    ).length;
    const highMathsMarks = filteredEnquiries.filter(
      (enquiry) => enquiry.prev_math > 90
    ).length;
    const highScienceMarks = filteredEnquiries.filter(
      (enquiry) => enquiry.prev_sci > 90
    ).length;
    const excellentStudents = filteredEnquiries.filter(
      (enquiry) => enquiry.prev_math > 95 && enquiry.prev_sci > 95
    ).length;

    return {
      total: filteredEnquiries.length,
      hostel: hostelStudents,
      highMaths: highMathsMarks,
      highScience: highScienceMarks,
      excellent: excellentStudents,
    };
  };

  const stats = getStatistics();

  return (
    <div className="scholarship-list-container">
      {permissions?.can_read ? (
        <>
          {/* Header Section */}
          <div className="scholarship-list-header">
            <div className="scholarship-list-header-left">
              <div className="scholarship-list-header-icon">
                <FaMedal />
              </div>
              <div className="scholarship-list-header-content">
                <h1 className="scholarship-list-title">
                  Scholarship List Management
                </h1>
                <p className="scholarship-list-subtitle">
                  Comprehensive scholarship applications and merit tracking
                </p>
              </div>
            </div>
            <div className="scholarship-list-header-right">
              <div className="scholarship-list-actions">
                <button
                  className="action-btn excel-btn"
                  onClick={downloadExcel}
                  disabled={filteredEnquiries.length === 0}
                >
                  <FaFileExcel />
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="scholarship-list-stats">
            <div className="stat-card primary">
              <div className="stat-icon">
                <FaUsers />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.total}</h3>
                <p className="stat-label">Total Applications</p>
              </div>
            </div>
            <div className="stat-card hostel">
              <div className="stat-icon">
                <FaHome />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.hostel}</h3>
                <p className="stat-label">Hostel Required</p>
              </div>
            </div>
            <div className="stat-card math">
              <div className="stat-icon">
                <FaChartBar />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.highMaths}</h3>
                <p className="stat-label">High Maths (90+)</p>
              </div>
            </div>
            <div className="stat-card science">
              <div className="stat-icon">
                <FaTrophy />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.highScience}</h3>
                <p className="stat-label">High Science (90+)</p>
              </div>
            </div>
            <div className="stat-card excellent">
              <div className="stat-icon">
                <FaStar />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{stats.excellent}</h3>
                <p className="stat-label">Excellent (95+ Both)</p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="scholarship-list-filters">
            <div className="filters-header">
              <div className="filters-header-left">
                <FaFilter />
                <h2>Filters & Search</h2>
              </div>
              <div className="filters-header-right">
                <span className="results-count">
                  {filteredEnquiries.length} of {enquiries.length} applications
                </span>
              </div>
            </div>

            <div className="filters-grid">
              {/* Search Filter */}
              <div className="filter-group search-group">
                <label className="filter-label">
                  <FaSearch />
                  <span>Search Applications</span>
                </label>
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <Input
                    placeholder="Search by name, roll no, email, aadhar, or phone..."
                    onChange={(value) => setSearchTerm(value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Date Range Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaCalendarAlt />
                  <span>Date Range</span>
                </label>
                <DateRangePicker
                  format="yyyy-MM-dd"
                  onChange={handleDateRangeChange}
                  value={dateRange}
                  className="filter-select"
                />
              </div>

              {/* Branch Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaSchool />
                  <span>Branch</span>
                </label>
                <SelectPicker
                  data={branches.map((opt) => ({ label: opt, value: opt }))}
                  onChange={setBranch}
                  value={branch}
                  cleanable={false}
                  searchable={false}
                  placeholder="Select Branch"
                  className="filter-select"
                />
              </div>

              {/* Session Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaCalendarAlt />
                  <span>Session</span>
                </label>
                <SelectPicker
                  data={sessionOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  onChange={setSelectedSession}
                  value={selectedSession}
                  cleanable={false}
                  searchable={false}
                  placeholder="Select Session"
                  className="filter-select"
                />
              </div>

              {/* Class Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaGraduationCap />
                  <span>Class</span>
                </label>
                <SelectPicker
                  data={classData}
                  onChange={handleClassFilterChange}
                  value={selectedClass}
                  cleanable={false}
                  searchable={false}
                  placeholder="Select Class"
                  className="filter-select"
                />
              </div>

              {/* Hostel Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaHome />
                  <span>Hostel</span>
                </label>
                <SelectPicker
                  data={hostelOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  onChange={setHostelFilter}
                  value={hostelFilter}
                  cleanable={false}
                  searchable={false}
                  className="filter-select"
                />
              </div>

              {/* Maths Marks Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaChartBar />
                  <span>Maths Marks</span>
                </label>
                <SelectPicker
                  data={marksOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  onChange={setMathsMarksFilter}
                  value={mathsMarksFilter}
                  cleanable={false}
                  searchable={false}
                  className="filter-select"
                />
              </div>

              {/* Science Marks Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <FaTrophy />
                  <span>Science Marks</span>
                </label>
                <SelectPicker
                  data={marksOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  onChange={setScienceMarksFilter}
                  value={scienceMarksFilter}
                  cleanable={false}
                  searchable={false}
                  className="filter-select"
                />
              </div>
            </div>
          </div>

          {/* Scholarship Table */}
          <div className="scholarship-list-table">
            {isLoading ? (
              <div className="loading-container">
                <Loader />
                <p>Loading scholarship applications...</p>
              </div>
            ) : (
              <EntranceTable
                listname="Scholarship List"
                data={filteredEnquiries}
                onSendEmail={(emailData) => {
                  handleSendEmails(
                    emailData.emails,
                    emailData.subject,
                    emailData.body
                  );
                }}
                navigateBaseUrl="/admin/scholarship"
                setSortOrder={setSortOrder}
                sortOrder={sortOrder}
              />
            )}
          </div>
        </>
      ) : (
        <div className="no-permission">
          <FaExclamationTriangle />
          <h2>Access Denied</h2>
          <p>You don't have permission to access this section.</p>
        </div>
      )}
    </div>
  );
};

export default AdminScholarshipList;
